// pms-adapters / supabase / takings — « Encaissements du jour », interim
// adapter (PMS-TERMINAL-LINK-BRIEF §8.8o).
//
// The Vega side of the TakingsBriefing port: one RPC, `pms_day_takings`, which
// sums the mirror's payment ledger (vega_payment) for the day by tender, and
// adds the Encaissement form's payments that Vega does not show yet. At go-live
// a PMS adapter over pms_payment replaces it.
//
// Read-only, so no L2 gate: the RPCs gate themselves on `pms.payment.read`.
//
// §8.8p adds the « Journée » register's two reads: `pms_day_others` (the day's
// payments for guests on neither sheet, each in its shift) and
// `pms_day_no_shows` (bookings due that day that Vega cancelled).

import type {
  DayTakingsLine,
  DeskShift,
  Instant,
  IsoDate,
  NoShow,
  OtherTaking,
  Result,
  TakingsBriefing,
  TakingsBucket,
} from '@lemontaigu/pms-core';
import { fromList, type SupabaseClient } from './support.js';

/** One row of `pms_day_takings` (20260926070100). */
export interface DayTakingsRow {
  property_code: string;
  bucket: string;
  label: string | null;
  in_cents: number;
  out_cents: number;
  movements: number;
  not_in_vega_cents: number;
  not_in_vega: number;
}

export const toDayTakingsLine = (r: DayTakingsRow): DayTakingsLine => ({
  propertyCode: r.property_code,
  bucket: r.bucket as TakingsBucket,
  label: r.label,
  inCents: Number(r.in_cents ?? 0),
  outCents: Number(r.out_cents ?? 0),
  movements: Number(r.movements ?? 0),
  notInVegaCents: Number(r.not_in_vega_cents ?? 0),
  notInVega: Number(r.not_in_vega ?? 0),
});

/** One row of `pms_day_others` (20260927010100). */
export interface OtherTakingRow {
  property_code: string;
  shift: string;
  source: string;
  kind: string | null;
  method: string | null;
  bucket: string;
  label: string | null;
  cents: number;
  at: string | null;
  resa: string | null;
  facture: string | null;
  party_label: string | null;
  arrival_date: string | null;
  departure_date: string | null;
}

export const toOtherTaking = (r: OtherTakingRow): OtherTaking => ({
  propertyCode: r.property_code,
  shift: (r.shift === 'checkout' ? 'checkout' : 'checkin') as DeskShift,
  source: r.source === 'form' ? 'form' : 'vega',
  kind: r.kind,
  method: r.method,
  bucket: r.bucket as TakingsBucket,
  label: r.label,
  cents: Number(r.cents ?? 0),
  at: r.at as Instant | null,
  bookingRef: r.resa,
  facture: r.facture,
  partyLabel: r.party_label,
  arrivalDate: r.arrival_date as IsoDate | null,
  departureDate: r.departure_date as IsoDate | null,
});

/** One row of `pms_day_no_shows` (20260927010100). */
export interface NoShowRow {
  property_code: string;
  resa: string;
  name: string | null;
  cancelled_on: string | null;
  cancelled_at: string | null;
  reason: string | null;
  rooms: number | null;
  lost_cents: number | null;
  clerical: boolean | null;
  payments: { date: string | null; at: string | null; kind: string; method: string | null; label: string | null;
              cents: number }[] | null;
}

export const toNoShow = (r: NoShowRow): NoShow => ({
  propertyCode: r.property_code,
  bookingRef: r.resa,
  name: r.name,
  cancelledOn: r.cancelled_on as IsoDate | null,
  cancelledAt: r.cancelled_at,
  reason: r.reason,
  rooms: r.rooms,
  lostCents: r.lost_cents === null || r.lost_cents === undefined ? null : Number(r.lost_cents),
  clerical: r.clerical === true,
  payments: (r.payments ?? []).map((p) => ({
    date: p.date as IsoDate | null,
    at: p.at as Instant | null,
    kind: p.kind,
    method: p.method,
    label: p.label,
    cents: Number(p.cents),
  })),
});

export class SupabaseVegaTakingsBriefing implements TakingsBriefing {
  constructor(private readonly db: SupabaseClient) {}

  async dayTakings(date: IsoDate): Promise<Result<DayTakingsLine[]>> {
    const r = await this.db.rpc('pms_day_takings', { p_date: date });
    return fromList<DayTakingsRow, DayTakingsLine>(
      r as { data: DayTakingsRow[] | null; error: typeof r.error },
      toDayTakingsLine,
    );
  }

  async dayOthers(date: IsoDate): Promise<Result<OtherTaking[]>> {
    const r = await this.db.rpc('pms_day_others', { p_date: date });
    return fromList<OtherTakingRow, OtherTaking>(r as { data: OtherTakingRow[] | null; error: typeof r.error }, toOtherTaking);
  }

  async dayNoShows(date: IsoDate): Promise<Result<NoShow[]>> {
    const r = await this.db.rpc('pms_day_no_shows', { p_date: date });
    return fromList<NoShowRow, NoShow>(r as { data: NoShowRow[] | null; error: typeof r.error }, toNoShow);
  }
}
